/**
 * Engagement measurement — scroll depth and form visibility (BTQ-90, 2026-08-13).
 *
 * WHY NOT GA4's BUILT-IN `scroll`
 * Enhanced measurement fires exactly once, at 90% of page height, and that is
 * the wrong ruler for this site: the enquiry form on the EB homepage sits at
 * roughly 70%, with a long editorial block and an FAQ below it. A visitor who
 * reached the form, filled it in and sent it can easily never trigger the 90%
 * event — so "37% scrolled" says nothing about "reached the form". These two
 * events replace that guesswork:
 *
 *   scroll_depth  percent_scrolled = 25 | 50 | 75 | 100, once each per route
 *   form_visible  form_id — the form block actually entered the viewport
 *
 * Together they turn the funnel question ("how many people got as far as the
 * form?") into a measured number instead of an estimate derived from bounce
 * rate, which is what BTQ-85 had to retract twice.
 *
 * SPA SEMANTICS
 * Both counters are per-route, not per-page-load: trackRouteChange has no
 * bearing here, so Analytics.tsx calls resetEngagement() on every navigation.
 * Without the reset a visitor who scrolled one page deep would never produce a
 * scroll_depth on any later page of the same visit.
 *
 * COST
 * The scroll listener is passive and rAF-throttled, and it detaches itself once
 * all four thresholds have fired — on a short page that is within the first
 * screen of scrolling. Forms are found by MutationObserver rather than polling,
 * because most of them mount inside lazily-loaded routes well after first paint.
 */

import { trackEvent } from "./analytics";
import { trafficType } from "./attribution";

const DEPTH_THRESHOLDS = [25, 50, 75, 100] as const;

/** Fraction of the form that must be on screen before it counts as seen. */
const FORM_VISIBLE_RATIO = 0.3;

/** Below this the document is treated as not scrolling — see currentDepthPercent. */
const MIN_SCROLLABLE_PX = 120;

/** Depth thresholds do not need frame-level resolution. */
const SCROLL_THROTTLE_MS = 200;

let initialised = false;
let scrollBound = false;
let throttleTimer: number | null = null;

let firedDepths = new Set<number>();
const seenFormKeys = new Set<string>();

let formObserver: IntersectionObserver | null = null;
let domObserver: MutationObserver | null = null;
const attachedForms = new WeakSet<Element>();

function pagePath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/* ------------------------------------------------------------ scroll depth */

/**
 * Null means "there is nothing to measure yet" — the document does not scroll.
 *
 * An earlier version returned 100 here, reasoning that a page shorter than the
 * viewport has been read in full. Measured on the live EB site 14.08: that path
 * fires all four thresholds at scrollY 0, because for the first moments after a
 * route change the markup has not landed and scrollHeight still equals the
 * viewport. A page nobody scrolled was being reported as fully read. Staying
 * silent costs only the genuinely short pages, of which these two sites have
 * none worth counting.
 */
function currentDepthPercent(): number | null {
  const doc = document.documentElement;
  const scrollable = doc.scrollHeight - doc.clientHeight;
  if (scrollable <= MIN_SCROLLABLE_PX) return null;
  const scrolled = window.scrollY || doc.scrollTop || document.body.scrollTop || 0;
  return Math.min(100, Math.round((scrolled / scrollable) * 100));
}

function evaluateDepth(): void {
  const percent = currentDepthPercent();
  if (percent === null) return;
  for (const threshold of DEPTH_THRESHOLDS) {
    if (percent < threshold || firedDepths.has(threshold)) continue;
    firedDepths.add(threshold);
    trackEvent("scroll_depth", {
      // String on purpose: the useful question is "how many people reached 50%",
      // which is a breakdown, not a sum. A number would arrive as epn.* and only
      // register as a custom metric, where summing percentages means nothing.
      percent_scrolled: String(threshold),
      page_path: pagePath(),
      traffic_bucket: trafficType(),
    });
  }
  if (firedDepths.size === DEPTH_THRESHOLDS.length) detachScroll();
}

/**
 * Time-throttled, not requestAnimationFrame-throttled. rAF is the usual choice
 * for scroll handlers, but it is frozen in background and hidden tabs, so the
 * depth of a page read in a tab the browser considers inactive would never be
 * recorded (this is also what made the first live verification come back empty).
 * Nothing here paints, so there is no reason to be tied to the frame clock.
 */
function onScroll(): void {
  if (throttleTimer !== null) return;
  throttleTimer = window.setTimeout(() => {
    throttleTimer = null;
    evaluateDepth();
  }, SCROLL_THROTTLE_MS);
}

function attachScroll(): void {
  if (scrollBound) return;
  scrollBound = true;
  window.addEventListener("scroll", onScroll, { passive: true });
}

function detachScroll(): void {
  if (!scrollBound) return;
  scrollBound = false;
  window.removeEventListener("scroll", onScroll);
}

/* --------------------------------------------------------- form visibility */

/**
 * Identity of a form, best effort and without touching the form components:
 * an explicit data-form-id wins, then the form's own id, then the id of the
 * section it lives in (EB's homepage form sits in <section id="consultation">).
 */
function resolveFormId(form: Element): string {
  const declared = form.closest("[data-form-id]")?.getAttribute("data-form-id");
  if (declared) return declared;
  if (form.id) return form.id;
  const section = form.closest("section[id], div[id]");
  return section?.id || "unknown";
}

function ensureFormObserver(): IntersectionObserver {
  if (formObserver) return formObserver;
  formObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        formObserver?.unobserve(entry.target);
        const id = resolveFormId(entry.target);
        const key = `${window.location.pathname}|${id}`;
        if (seenFormKeys.has(key)) continue;
        seenFormKeys.add(key);
        trackEvent("form_visible", {
          form_id: id,
          page_path: pagePath(),
          traffic_bucket: trafficType(),
        });
      }
    },
    { threshold: FORM_VISIBLE_RATIO },
  );
  return formObserver;
}

function scanForms(): void {
  const observer = ensureFormObserver();
  document.querySelectorAll("form").forEach((form) => {
    if (attachedForms.has(form)) return;
    attachedForms.add(form);
    observer.observe(form);
  });
}

/**
 * Height has to settle before an unscrolled page can be judged. Immediately
 * after a route change the new markup has not rendered yet, so scrollHeight is
 * still roughly the viewport — evaluating then would read "the page fits the
 * screen" and fire all four thresholds on a page the visitor has not seen.
 */
const SETTLE_MS = 1200;
let settleTimer: number | null = null;

function evaluateAfterSettle(): void {
  if (settleTimer !== null) window.clearTimeout(settleTimer);
  settleTimer = window.setTimeout(() => {
    settleTimer = null;
    evaluateDepth();
  }, SETTLE_MS);
}

/* --------------------------------------------------------------------- api */

/** Called once, alongside initAnalytics(). */
export function initEngagement(): void {
  if (initialised || typeof window === "undefined") return;
  initialised = true;
  attachScroll();
  evaluateAfterSettle();
  scanForms();
  domObserver = new MutationObserver(() => scanForms());
  domObserver.observe(document.body, { childList: true, subtree: true });
}

/** Called on every route change: thresholds and form identity are per-route. */
export function resetEngagement(): void {
  if (!initialised) return;
  firedDepths = new Set();
  attachScroll();
  // The new route's markup lands after this effect; the MutationObserver picks
  // up its forms, this call only covers markup that is already in place.
  scanForms();
  evaluateAfterSettle();
}
